import { useCallback, useEffect, useState, useMemo } from 'react';
import { prop } from 'ramda';
import { noop } from 'js/utils/methods.util';
import * as urlUtil from 'js/utils/url.util';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'js/hooks/navigate';
import { useDeviceSelector } from 'js/store/hooks';
import { useIsAllowedToRedirect } from 'js/hooks/useIsAllowedToRedirect';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useSubscribeOnLocationChange } from 'js/hooks/useSubscribeOnLocationChange';

import * as Types from '../types';

export const useBackButton = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const isAllowedToRedirect = useIsAllowedToRedirect();
    const navigateWithReplace = useNavigate({ replace: true });
    const [isShowBackButton, setIsShowBackButton] = useState(false);

    const toggleBackButton = useCallback((): void => {
        const { showBack } = window.history.state || {};
        const backURL = urlUtil.getLocationParam('back_url');

        setIsShowBackButton(Boolean(backURL || showBack));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.history.state?.showBack]);

    const handleBackButtonClick = useCallback((): void => {
        const backURL = urlUtil.getLocationParam('back_url');

        navigateWithReplace(pathname, undefined, { removeParams: ['back_url'] });

        if (backURL) {
            if (isAllowedToRedirect(backURL)) {
                navigate(backURL);
            }
        } else {
            window.history.back();
        }
    }, [isAllowedToRedirect, pathname, navigateWithReplace, navigate]);

    useSubscribeOnLocationChange(toggleBackButton);

    useEffect(() => {
        toggleBackButton();
        publicEventsAPIService.on('header:backButton:toggle', toggleBackButton);

        return () => {
            publicEventsAPIService.off('header:backButton:toggle', toggleBackButton);
        };
    }, [toggleBackButton]);

    return useMemo((): Types.IBackButtonSettings => ({
        isShowBackButton: isDesktop ? false : isShowBackButton,
        handleBackButtonClick: isDesktop ? noop : handleBackButtonClick
    }), [isShowBackButton, handleBackButtonClick, isDesktop]);
};
